import { VisuallyHidden } from "@mantine/core";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from "react";
import { createPortal } from "react-dom";

import type { AchievementBoard, AchievementView, Tier, UnseenAward } from "../../api/generated/model";
import { draw } from "../case/Celebration";
import { emptyScene, step } from "../case/effects";
import { CARDS_KEPT, readPref, writePref } from "../prefs";
import { useUploadTrayTop } from "../uploads/UploadsProvider";
import { Badge } from "./Badge";
import { useBoard, useMarkSeen } from "./board";
import { fireworks } from "./fireworks";
import classes from "./unlock.module.css";
import { TIER_NAME, tierColor } from "./words";

const stillMotion = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;

/** The card's fireworks on one canvas over the page, which the pointer passes through. */
function Fireworks({ tier, card }: { tier: Tier; card: RefObject<HTMLElement | null> }) {
  const canvas = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const element = canvas.current;
    const box = card.current?.getBoundingClientRect();
    if (element === null || box === undefined) return;
    const ratio = Math.min(window.devicePixelRatio || 1, 2);
    const [width, height] = [window.innerWidth, window.innerHeight];
    element.width = width * ratio;
    element.height = height * ratio;
    const context = element.getContext("2d");
    context?.setTransform(ratio, 0, 0, ratio, 0, 0);
    const scene = emptyScene();
    const color = getComputedStyle(document.documentElement).getPropertyValue(`--cai-tier-${tier}`).trim();
    let frame = 0;
    const loop = () => {
      const alive = step(scene, height);
      if (context !== null) draw(context, scene, width, height);
      frame = alive ? requestAnimationFrame(loop) : 0;
    };
    const timers = fireworks(tier, color, box, Math.random).map((beat) =>
      window.setTimeout(() => {
        beat.run(scene);
        if (frame === 0) frame = requestAnimationFrame(loop);
      }, beat.at),
    );
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      cancelAnimationFrame(frame);
    };
  }, [tier, card]);
  // On the body: the card's own transform would make it the frame of anything fixed inside it.
  return createPortal(<canvas className={classes.fireworks} ref={canvas} aria-hidden />, document.body);
}

/** One unlock's card: it holds while hovered or focused, and after its time flies into the gift icon. */
function Card({ view, onOpen, onDone }: { view: AchievementView; onOpen: () => void; onDone: () => void }) {
  const card = useRef<HTMLDivElement>(null);
  const time = useRef<HTMLElement>(null);
  // The pointer and the keyboard hold it each on its own: leaving one while the other stays keeps
  // it (decision 11).
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const held = hovered || focused;
  const still = stillMotion();

  /** Into the gift icon, which pulses as it takes the card; with reduced motion, simply gone. */
  const flyAway = useCallback(() => {
    const element = card.current;
    const icon = document.querySelector<HTMLElement>("[data-gift-icon]");
    if (still || element === null || icon === null || typeof element.animate !== "function") return onDone();
    const from = element.getBoundingClientRect();
    const to = icon.getBoundingClientRect();
    const x = to.left + to.width / 2 - (from.left + from.width / 2);
    const y = to.top + to.height / 2 - (from.top + from.height / 2);
    const flight = element.animate(
      [{ transform: "none", opacity: 1 }, { transform: `translate(${x}px, ${y}px) scale(0.08)`, opacity: 0.2 }],
      { duration: 650, easing: "cubic-bezier(0.6, 0, 0.8, 0.4)", fill: "forwards" },
    );
    flight.onfinish = () => {
      icon.animate?.(
        [{ transform: "none" }, { transform: "scale(1.18) rotate(-9deg)" }, { transform: "scale(0.96) rotate(5deg)" }, { transform: "none" }],
        { duration: 900, easing: "cubic-bezier(0.2, 0.8, 0.2, 1)" },
      );
      onDone();
    };
  }, [still, onDone]);

  // Its time bar's animation is its six seconds; while held, the bar pauses and nothing listens.
  useEffect(() => {
    const bar = time.current;
    if (bar === null || held) return;
    bar.addEventListener("animationend", flyAway);
    return () => bar.removeEventListener("animationend", flyAway);
  }, [held, flyAway]);

  return (
    <div
      ref={card}
      className={classes.card}
      data-held={held || undefined}
      style={{ "--tier": tierColor(view.tier) } as CSSProperties}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onFocus={() => setFocused(true)}
      onBlur={(event) => setFocused(event.currentTarget.contains(event.relatedTarget))}
    >
      {still ? null : <Fireworks tier={view.tier} card={card} />}
      <button
        type="button"
        className={classes.open}
        aria-label={`${view.name}, ${TIER_NAME[view.tier]}: see your achievements`}
        onClick={onOpen}
      >
        <Badge tier={view.tier} glyph={view.glyph} state={still ? "earned" : "new"} size={92} />
        <span className={classes.words}>
          <span className={classes.kicker}>Achievement unlocked</span>
          <span className={classes.name}>{view.name}</span>
          <span className={classes.tier}>{TIER_NAME[view.tier]}</span>
          <span className={classes.roast}>{view.description}</span>
        </span>
      </button>
      <button type="button" className={classes.close} aria-label="Put it away" onClick={onDone}>
        ×
      </button>
      {/* The card's six seconds: hovering or focusing it pauses them (WCAG 2.2.1). */}
      <div className={classes.timer}>
        <i ref={time} />
      </div>
    </div>
  );
}

/**
 * The card's room on the right: down to its corner, or to just above the upload tray while that
 * shows, so the card never covers what the tray says (spec web/unlock-card-above-uploads).
 */
function Room({ children }: { children: ReactNode }) {
  const tray = useUploadTrayTop();
  return (
    <div className={classes.room} style={{ "--tray-top": `${tray}px` } as CSSProperties}>
      {children}
    </div>
  );
}

/** An unseen unlock and what its card shows. */
type Unlock = [UnseenAward, AchievementView];

/** The unseen unlocks this browser has no card of yet, oldest first, each with what it shows. */
function queued(board: AchievementBoard, played: ReadonlySet<string>): Unlock[] {
  return board.unseen
    .filter((award) => !played.has(award.award_id))
    .reverse()
    .flatMap((award) => {
      const view = board.achievements.find((each) => each.id === award.achievement_id);
      return view === undefined ? [] : [[award, view] as Unlock];
    });
}

/**
 * The unlock cards over a team's pages, none while the board is unread or achievements are hidden.
 * The shell mounts them beside its header, never inside it: a full-window page makes the header
 * inert, which would silence their announcement for a screen reader.
 */
export function UnlockCards({ teamId, brandId }: { teamId: string; brandId?: string }) {
  const board = useBoard().data;
  return board === undefined || board.hidden ? null : <Queue board={board} teamId={teamId} brandId={brandId} />;
}

/**
 * The unlock card (spec web/achievements, 4): its own animated component, never a toast system.
 * One card at a time, for each unseen unlock this browser has not shown, oldest first, with a
 * polite announcement; none while achievements are hidden. The card on screen is held from its
 * first render to the end of its show, so the menu or the tab marking its unlock seen takes it off
 * the list, never off the screen (decision 9).
 */
function Queue(props: { board: AchievementBoard; teamId: string; brandId: string | undefined }) {
  const { board, teamId, brandId } = props;
  // Every card this page has played, so its queue plays each once however long the backlog; the
  // pref keeps only the last `CARDS_KEPT` of them, for the pages after (decision 18).
  const [played, setPlayed] = useState<ReadonlySet<string>>(() => new Set(readPref("achievementCards")));
  const [showing, setShowing] = useState<Unlock | null>(null);
  const { mark } = useMarkSeen();
  // The achievements' tab this frame has: Classic's Account tab, or Pro's Settings tab when Pro's
  // shell passes its brand (spec ui-perf/pro-unlock-cards).
  const matchRoute = useMatchRoute();
  const onTab =
    (brandId === undefined
      ? matchRoute({ to: "/teams/$teamId/account/achievements", params: { teamId } })
      : matchRoute({ to: "/teams/$teamId/brands/$brandId/home2/settings/achievements", params: { teamId, brandId } })) !==
    false;
  const navigate = useNavigate();
  const next = board.hidden ? undefined : queued(board, played)[0];
  // Adjusted while rendering, as React allows for state that follows its props: a new card is
  // taken up, or Hide puts the one on screen away.
  if (board.hidden && showing !== null) setShowing(null);
  if (!board.hidden && showing === null && next !== undefined) setShowing(next);
  const done = (awardId: string) => {
    const kept = readPref("achievementCards").filter((id) => id !== awardId);
    writePref("achievementCards", [...kept, awardId].slice(-CARDS_KEPT));
    setPlayed((before) => new Set(before).add(awardId));
    setShowing(null);
  };
  // Opened, it is seen: a tab already open marks it here, as its opening has passed, and shows the
  // mark's refusal, if any (decision 16); a tab that opens now marks it as it opens, and shows it
  // develop (decision 20).
  const open = (awardId: string) => {
    if (onTab) mark([awardId]);
    done(awardId);
    void (brandId === undefined
      ? navigate({ to: "/teams/$teamId/account/achievements", params: { teamId } })
      : navigate({ to: "/teams/$teamId/brands/$brandId/home2/settings/achievements", params: { teamId, brandId } }));
  };
  // The announcement stays in the page, there before any card it announces; only a card on screen
  // goes on the body, so a page with none leaves nothing outside the app's root.
  return (
    <>
      <VisuallyHidden aria-live="polite">{showing === null ? "" : `Achievement unlocked: ${showing[1].name}`}</VisuallyHidden>
      {showing === null
        ? null
        : createPortal(
            <Room>
              <Card
                key={showing[0].award_id}
                view={showing[1]}
                onOpen={() => open(showing[0].award_id)}
                onDone={() => done(showing[0].award_id)}
              />
            </Room>,
            document.body,
          )}
    </>
  );
}
